import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import BreadcrumbTwo from '../../../../components/Breadcrumbs/BreadcrumbTwo';
import BasicPagination from '../../../../components/DataTable/BasicPagination';
import DatePicker from '../../../../components/Forms/DatePicker/DatePicker';
import SearchInputField from '../../../../components/Forms/Input/SearchInputField';
import SelectField from '../../../../components/Forms/Input/SelectField';
import SectionWrapper from '../../../../components/SectionWrapper/SectionWrapper';
import { RESTAURANT_INVENTORY_UPDATED_EVENT } from '../../production/restaurantInventoryEvents';
import useApiErrorHandler from '../../../../hooks/useApiErrorHandler';
import useDebouncedValue from '../../../../hooks/useDebouncedValue';
import BranchService from '../../../../services/branch/BranchService';
import { useAuthUser } from '../../../../hooks/auth/useAuthUser';
import RestaurantRawMaterialCategoryService from '../../../../services/restaurant/RestaurantRawMaterialCategoryService';
import RestaurantProductionInventoryService from '../../../../services/restaurant/RestaurantProductionInventoryService';
import { IBranchDropdown } from '../../../../types/branches/branchInterfaces';
import { RestaurantInventoryMovement } from '../../../../types/restaurant/RestaurantProductionInventoryTypes';
import { RestaurantRawMaterialCategoryDTO } from '../../../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { PageMetadata } from '../../../../types/restaurant/PagedResponse';
import {
  compareDecimal,
  formatDecimal,
  formatMoney,
} from '../../../../utils/decimalValue';
import WorkspaceLayout from '../../../../layout/WorkspaceLayout';

const prettify = (value: string) =>
  value
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/^\w/, (char) => char.toUpperCase());

/** Enum RestaurantInventoryMovementType → etiqueta legible para el usuario. */
const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  PURCHASE: 'Compra (entrada)',
  PURCHASE_IN: 'Compra (entrada)',
  ADJUSTMENT_IN: 'Ajuste (entrada)',
  ADJUSTMENT_OUT: 'Ajuste (salida)',
  WASTE: 'Merma',
  PRODUCTION_CONSUMPTION: 'Consumo por receta/producción',
};

export const restaurantMovementLabel = (type: string) =>
  MOVEMENT_TYPE_LABELS[type] ?? prettify(type);

const INCOMING_MOVEMENT_TYPES = new Set([
  'PURCHASE',
  'PURCHASE_IN',
  'ADJUSTMENT_IN',
]);

/** Tipo de referencia (origen del movimiento) → etiqueta legible. */
const REFERENCE_TYPE_LABELS: Record<string, string> = {
  RESTAURANT_PURCHASE_ORDER_DETAIL: 'Orden de compra',
  RESTAURANT_PURCHASES: 'Compra',
  RESTAURANT_PRODUCTION_BATCH_ITEM: 'Lote de producción',
  RESTAURANT_ORDER_ITEM: 'Línea de comanda',
  RESTAURANT_ORDERS_DETAIL: 'Línea de comanda',
  RESTAURANT_ORDER_MODIFIER: 'Modificador de comanda',
  RESTAURANT_WASTE: 'Merma registrada',
  RESTAURANT_SALES_SUMMARY: 'Resumen de ventas',
  SALE_LINE: 'Línea de venta',
  CORPORATE_CAFETERIA_ITEM: 'Cafetería corporativa',
};

export const restaurantMovementReferenceLabel = (
  type: string | null | undefined
) => (type ? REFERENCE_TYPE_LABELS[type] ?? prettify(type) : '—');

const RestaurantInventoryMovementsScreen = () => {
  const { hasRole } = useAuthUser();
  const canPickBranch =
    hasRole('ADMIN') || hasRole('SUPPORT') || hasRole('ACCOUNTANT');

  const [branches, setBranches] = useState<IBranchDropdown[]>([]);
  const [branchId, setBranchId] = useState<number | ''>('');
  const [movements, setMovements] = useState<RestaurantInventoryMovement[]>([]);
  const [categories, setCategories] = useState<RestaurantRawMaterialCategoryDTO[]>([]);
  const [pageMetadata, setPageMetadata] = useState<PageMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState(0);
  const [movementType, setMovementType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const debouncedSearch = useDebouncedValue(search, 350);
  const { handle } = useApiErrorHandler();
  useEffect(() => {
    const request = canPickBranch
      ? BranchService.fetchDropdown()
      : BranchService.fetchMyBranch().then((mine) => [mine]);

    request
      .then((items) => {
        setBranches(items);
        if (items.length) setBranchId(items[0].id);
      })
      .catch(handle)
      .finally(() =>
        setLoading(false)
      ); /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [canPickBranch]);
  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    try {
      const response = await RestaurantProductionInventoryService.listMovements(branchId, {
        search: debouncedSearch.trim() || undefined,
        categoryId: categoryId || undefined,
        movementType: movementType || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        page,
        size,
      });
      setMovements(response.content);
      setPageMetadata(response.page);
    } catch (error) {
      handle(error);
    } finally {
      setLoading(false);
    } /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [branchId, categoryId, dateFrom, dateTo, debouncedSearch, movementType, page, size]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const listener = () => void load();
    window.addEventListener(RESTAURANT_INVENTORY_UPDATED_EVENT, listener);
    return () =>
      window.removeEventListener(RESTAURANT_INVENTORY_UPDATED_EVENT, listener);
  }, [load]);
  useEffect(() => {
    if (!branchId) return;
    RestaurantRawMaterialCategoryService.listFiltered(branchId, {
      page: 0,
      size: 100,
      sort: ['name,asc'],
      filters: { search: null, active: true },
    }).then((response) => setCategories(response.content)).catch(handle);
  }, [branchId]);

  return (
    <WorkspaceLayout>
      <BreadcrumbTwo />
      <SectionWrapper>
        <div className="border-b border-stroke p-5 dark:border-strokedark sm:px-7">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex gap-3">
              <span className="rounded-xl bg-primary/10 p-3 text-primary">
                <ArrowLeftRight className="h-6 w-6" />
              </span>
              <div>
                <h1 className="text-xl font-semibold text-black dark:text-white">
                  Movimientos de inventario
                </h1>
                <p className="text-sm text-bodydark2">
                  Trazabilidad de entradas, salidas y consumos por receta.
                </p>
              </div>
            </div>
            {canPickBranch && (
              <div className="w-full lg:w-80">
                <SelectField
                  label="Sucursal"
                  name="movementBranch"
                  value={branchId}
                  iconKey="building"
                  options={branches.map((branch) => ({
                    value: branch.id,
                    label: branch.name,
                  }))}
                  onChange={(e) => { setPage(0); setBranchId(Number(e.target.value)); }}
                />
              </div>
            )}
          </div>
        </div>
        <div className="grid gap-4 border-b border-stroke p-5 dark:border-strokedark md:grid-cols-2 xl:grid-cols-5 xl:items-end">
          <div className="xl:pb-5">
            <SearchInputField
              label="Buscar"
              name="movementSearch"
              value={search}
              iconKey="search"
              placeholder="Materia prima o referencia..."
              withMargin={false}
              onChange={(event) => { setSearch(event.target.value); setPage(0); }}
            />
          </div>
          <SelectField
            label="Categoría"
            name="movementCategory"
            value={categoryId}
            options={[
              { value: 0, label: 'Todas las categorías' },
              ...categories.map((category) => ({ value: category.id, label: category.name })),
            ]}
            onChange={(event) => { setCategoryId(Number(event.target.value)); setPage(0); }}
          />
          <SelectField
            label="Tipo de movimiento"
            name="movementType"
            value={movementType}
            placeholder="Todos los tipos"
            options={[
              { value: '', label: 'Todos los tipos' },
              { value: 'PURCHASE_IN', label: 'Compra (entrada)' },
              { value: 'ADJUSTMENT_IN', label: 'Ajuste (entrada)' },
              { value: 'ADJUSTMENT_OUT', label: 'Ajuste (salida)' },
              { value: 'WASTE', label: 'Merma' },
              {
                value: 'PRODUCTION_CONSUMPTION',
                label: 'Consumo por receta/producción',
              },
            ]}
            onChange={(event) => { setMovementType(String(event.target.value)); setPage(0); }}
          />
          <DatePicker label="Desde" name="movementDateFrom" value={dateFrom} iconKey="calendar" onChange={(value) => { setDateFrom(value); setPage(0); }} />
          <DatePicker label="Hasta" name="movementDateTo" value={dateTo} iconKey="calendar" onChange={(value) => { setDateTo(value); setPage(0); }} />
        </div>
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1100px] text-left text-sm">
              <thead className="bg-gray-2 text-xs uppercase text-bodydark2 dark:bg-meta-4">
                <tr>
                  <th className="p-4">Tipo</th>
                  <th className="p-4">Materia prima</th>
                  <th className="p-4">Referencia</th>
                  <th className="p-4">Cantidad</th>
                  <th className="p-4">Stock anterior</th>
                  <th className="p-4">Stock nuevo</th>
                  <th className="p-4">Costo unitario</th>
                  <th className="p-4">Costo total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stroke dark:divide-strokedark">
                {movements.map((item) => (
                  <tr key={item.id}>
                    <td className="p-4">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-medium ${
                          INCOMING_MOVEMENT_TYPES.has(item.movementType)
                            ? 'bg-success/10 text-success'
                            : 'bg-warning/15 text-warning'
                        }`}
                      >
                        {restaurantMovementLabel(item.movementType)}
                      </span>
                    </td>
                    <td className="p-4 font-medium text-black dark:text-white">
                      {item.rawMaterialCode && `${item.rawMaterialCode} · `}
                      {item.rawMaterialName}
                    </td>
                    <td className="p-4">
                      <span className="text-black dark:text-white">
                        {restaurantMovementReferenceLabel(item.referenceType)}
                      </span>
                      {item.referenceId != null && (
                        <span className="text-bodydark2">
                          {' '}
                          · N.º {item.referenceId}
                        </span>
                      )}
                    </td>
                    <td className="p-4">
                      {formatDecimal(item.quantity)} {item.unitCode}
                    </td>
                    <td className="p-4">{formatDecimal(item.previousStock)}</td>
                    <td
                      className={`p-4 ${
                        compareDecimal(item.newStock, 0) < 0
                          ? 'font-semibold text-danger'
                          : ''
                      }`}
                    >
                      {formatDecimal(item.newStock)}
                    </td>
                    <td className="p-4">{formatMoney(item.unitCost)}</td>
                    <td className="p-4 font-semibold">
                      {formatMoney(item.totalCost)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {movements.length === 0 && (
              <p className="p-12 text-center text-bodydark2">
                No hay movimientos para mostrar.
              </p>
            )}
          </div>
        )}
        {pageMetadata && (
          <BasicPagination
            currentPage={pageMetadata.number}
            totalPages={pageMetadata.totalPages}
            totalElements={pageMetadata.totalElements}
            size={pageMetadata.size}
            first={pageMetadata.number === 0}
            last={pageMetadata.number >= pageMetadata.totalPages - 1}
            onPageChange={setPage}
            onSizeChange={(value) => { setSize(value); setPage(0); }}
          />
        )}
      </SectionWrapper>
    </WorkspaceLayout>
  );
};

export default RestaurantInventoryMovementsScreen;
